<script lang="ts">

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        Small,
        dateShort,
        SubscriptionDelete,
        SubscriptionDuplicate,
        Icon,
        InputToggle,
        SUBSCRIPTION_API,
        Card,
        Heading,
        OutputAmount,
        OutputPrice,
    } from '@smatch/cassiopeia-web-components';

    // TYPES
    import type { Subscription } from 'src/app/types';


    // UTILITIES
    import { navigate } from 'svelte-navigator';

    /* PROPS --------------------------------------------------------------------------------- */

    export let subscription: Subscription;

    /* INTERNAL --------------------------------------------------------------------------------- */

    const handleEdit = (): void => {
        navigate(`/subscriptions/${ subscription._.key }`);
    };

    const toggleNotifications = (event: Event): void => {
        const REQUEST: {
            state: 'created' | 'paused';
        } = { state: 'created' };

        const TARGET = event.target as HTMLInputElement;

        if (!TARGET.checked) {
            REQUEST.state = 'paused';
        }
        void SUBSCRIPTION_API.updateSubscriptionState(
            { subscriptionKey: subscription!._.key },
            REQUEST,
        );
    };

    const toggleSimilarBrands = (event: Event): void => {
        const TARGET = event.target as HTMLInputElement;
        const REQUEST: SUBSCRIPTION_API.UpdateSubscriptionRequest = {
            constraints: {
                ...subscription.constraints,
                allowsSimilarBrands: TARGET.checked,
            },
        };

        void SUBSCRIPTION_API.updateSubscription(
            { subscriptionKey: subscription!._.key },
            REQUEST,
        );
    };

    const getTrimmedName = (name: string | undefined): string => {
        if (name === undefined) {
            return 'Not specified';
        }

        try {
            return name.replace(/^[^:]+: /u, '');
        } catch (error) {
            return name;
        }
    };

</script>

<tr class = 's8-table-row'>
    <td>
        { getTrimmedName(subscription.name) }
    </td>

    <td>
        <OutputPrice
            discount = { subscription.constraints.minimum?.discount }
            price = { subscription.constraints.maximumUnitPrice }
            currencyCode = { subscription.currencyCode }
            unit = { subscription.constraints.priceUnit }
        />
    </td>

    <td>
        <OutputAmount
            value = { subscription.constraints.minimum?.value }
            currencyCode = { subscription.currencyCode }
            quantityCode = { subscription.constraints.minimum?.quantityCode }
        />
    </td>

    <td>
        {#if subscription.expiresAt}
            <div>{ dateShort(subscription.expiresAt) }</div>
        {:else}
            <Small>Not specified</Small>
        {/if}
    </td>
    <td class = 's8-align-center'>
        {#if subscription.constraints.brandKeys !== undefined && subscription.constraints.brandKeys.length > 0}
            <InputToggle
                size = 'md'
                name = 'subscription.allowsSimilarBrands'
                isChecked = { subscription.constraints.allowsSimilarBrands }
                isDisabled = { subscription._.state !== 'created' }
                color = 'quaternary'
                on:change = { toggleSimilarBrands }
                css = '!w-auto !inline'
            />
            {#if subscription.constraints.allowsSimilarBrands}
                Enabled
            {:else}
                Disabled
            {/if}
        {:else}
            <Small>No brands selected</Small>
        {/if}
    </td>
    <td class = 's8-align-center'>
        <InputToggle
            size = 'md'
            name = 'subscription.state'
            isChecked = { subscription._.state === 'created' }
            color = 'quaternary'
            on:change = { toggleNotifications }
            css = '!w-auto !inline'
        />
        {#if subscription._.state === 'created'}
            Enabled
        {:else}
            Paused
        {/if}
    </td>

    <td>
        <div class = 's8-actions'>
            <Icon key = 'edit' label = 'Edit' on:click = { handleEdit } />
            <SubscriptionDuplicate { subscription } />
            <SubscriptionDelete subscriptionKey = { subscription._.key } />
        </div>
    </td>
</tr>


<!-- Cards instead of table rows for a nicer mobile view -->
<div class = 's8-card'>
    <Card>
        <div class = 's8-card-row-of-3'>
            <div>
                <Heading size = 'xxs'>Brands & Categories</Heading>
                { getTrimmedName(subscription.name) }
            </div>
            <div>
                <Heading size = 'xxs'>Pricing</Heading>
                <OutputPrice
                    discount = { subscription.constraints.minimum?.discount }
                    price = { subscription.constraints.maximumUnitPrice }
                    currencyCode = { subscription.currencyCode }
                    unit = { subscription.constraints.priceUnit }
                />
            </div>
            <div>
                <Heading size = 'xxs'>Minimum Amount</Heading>
                <OutputAmount
                    value = { subscription.constraints.minimum?.value }
                    currencyCode = { subscription.currencyCode }
                    quantityCode = { subscription.constraints.minimum?.quantityCode }
                />
            </div>
        </div>

        <div class = 's8-card-row-of-3'>
            <div>
                <Heading size = 'xxs'>Expiration Date</Heading>
                {#if subscription.expiresAt}
                    <div>{ dateShort(subscription.expiresAt) }</div>
                {:else}
                    <Small>Not specified</Small>
                {/if}
            </div>
            <div>
                <Heading size = 'xxs'>Recommend Similar Brands</Heading>
                {#if subscription.constraints.brandKeys !== undefined && subscription.constraints.brandKeys.length > 0}
                    <InputToggle
                        size = 'md'
                        name = 'subscription.allowsSimilarBrands'
                        isChecked = { subscription.constraints.allowsSimilarBrands }
                        isDisabled = { subscription._.state !== 'created' }
                        color = 'quaternary'
                        on:change = { toggleSimilarBrands }
                        css = '!w-auto !inline'
                    />
                    {#if subscription.constraints.allowsSimilarBrands}
                        Enabled
                    {:else}
                        Disabled
                    {/if}
                {:else}
                    <Small>No brands selected</Small>
                {/if}
            </div>
            <div>
                <Heading size = 'xxs'>Notifications</Heading>
                <InputToggle
                    size = 'md'
                    name = 'subscription.state'
                    isChecked = { subscription._.state === 'created' }
                    color = 'quaternary'
                    on:change = { toggleNotifications }
                    css = '!w-auto !inline'
                />
                {#if subscription._.state === 'created'}
                    Enabled
                {:else}
                    Paused
                {/if}
            </div>
            <div>
                <div class = 's8-actions'>
                    <Icon key = 'edit' label = 'Edit' on:click = { handleEdit } />
                    <SubscriptionDuplicate { subscription } />
                    <SubscriptionDelete subscriptionKey = { subscription._.key } />
                </div>
            </div>
        </div>
    </Card>
</div>

<style lang="postcss">
    tr {
        background-color: white;
        height: 40px;
    }

    td.s8-align-center {
        text-align: center;
    }

    div.s8-actions {
        display: flex;
        float: inline-end;
        column-gap: 5px;
    }

    div.s8-card {
        display: none;

        div.s8-actions {
            margin-top: 20px;
        }
    }

    div.s8-card-row-of-3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        margin-bottom: 15px;

        :global(h6) {
            margin-bottom: 5px;
        }
    }

    @media (max-width: 700px) {
        tr {
            display: none;
        }
        div.s8-card {
            display: block;
        }
    }
</style>